// eslint-disable-next-line no-restricted-imports -- Adapter is the only place allowed to import Firebase directly
import {
  auth,
  googleProvider,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  onAuthStateChanged,
  type FirebaseUser,
} from '@/shared/config/firebase';
import type { AuthService, AuthUser } from '../ports/auth-service.port';

/**
 * Maps Firebase User to domain AuthUser.
 */
function mapFirebaseUser(firebaseUser: FirebaseUser): AuthUser {
  return {
    uid: firebaseUser.uid,
    email: firebaseUser.email,
    displayName: firebaseUser.displayName,
    photoURL: firebaseUser.photoURL,
  };
}

/**
 * Firebase implementation of AuthService port.
 * This adapter encapsulates all Firebase-specific logic.
 */
export const firebaseAuthAdapter: AuthService = {
  async loginWithEmail(email: string, password: string): Promise<AuthUser> {
    const credential = await signInWithEmailAndPassword(auth, email, password);
    return mapFirebaseUser(credential.user);
  },

  async loginWithGoogle(): Promise<AuthUser> {
    const credential = await signInWithPopup(auth, googleProvider);
    return mapFirebaseUser(credential.user);
  },

  async logout(): Promise<void> {
    await signOut(auth);
  },

  async getIdToken(): Promise<string | null> {
    const user = auth.currentUser;
    if (!user) return null;
    return user.getIdToken(true);
  },

  getCurrentUser(): AuthUser | null {
    const user = auth.currentUser;
    if (!user) return null;
    return mapFirebaseUser(user);
  },

  onAuthStateChanged(callback: (user: AuthUser | null) => void): () => void {
    return onAuthStateChanged(auth, (firebaseUser) => {
      callback(firebaseUser ? mapFirebaseUser(firebaseUser) : null);
    });
  },
};
